import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import SettingsBars from "../components/SettingsBars";
import { Flex } from "../components/shared/Flex";
import { useCameras, useDisableZoom } from "../hooks";
import { isMobile, isTablet } from "@zakhealth/web-sdk";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import Loader from "./Loader";
import ZakHealthSignalMonitor from "components/ZakHealthSignalMonitor";
import { Constants } from "constants/api";
import { describeError, logger } from "utils/logger";

const Container = styled(Flex) <{ isSettingsOpen: boolean }>`
  height: 100%;
  width: 100%;
  position: relative;
  flex-direction: column;
  justify-content: start;
  align-items: center;
  background-color: ${({ isSettingsOpen }) =>
        isSettingsOpen && "rgba(0, 0, 0, 0.5)"};
`;

const UnsupportedDeviceContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
`;

const UnsupportedContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 40px 30px;
  max-width: 500px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const WarningIcon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #333;
  margin-bottom: 16px;
`;

const Message = styled.p`
  font-size: 18px;
  color: #555;
  margin-bottom: 12px;
  line-height: 1.6;
`;

const SubMessage = styled.p`
  font-size: 14px;
  color: #888;
  line-height: 1.5;
`;

const ScanPage = () => {
    const cameras = useCameras();
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [cameraId, setCameraId] = useState<string>();
    const [isLicenseValid, setIsLicenseValid] = useState(false);
    const [isValidUser, setIsValidUser] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // Enhanced device detection for Android tablets (Lenovo, Samsung, etc.) and iPads
    const detectMobileOrTablet = () => {
        const ua = navigator.userAgent.toLowerCase();
        const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        const maxDimension = Math.max(screenWidth, screenHeight);
        const minDimension = Math.min(screenWidth, screenHeight);
        const aspectRatio = maxDimension / minDimension;

        // 1. Try ZakHealth SDK detection first
        const sdkMobile = isMobile();
        const sdkTablet = isTablet();
        // console.log("sdkMobile", sdkMobile);
        // console.log("sdkTablet", sdkTablet);

        // 2. Check for Android tablets explicitly (Lenovo, Samsung, etc.)
        const isAndroidTablet = /android/i.test(ua) && !/mobile/i.test(ua);

        // 3. Check for iPad (including iPadOS 13+ pretending to be Mac)
        const isIPad =
            /ipad/i.test(ua) ||
            (/macintosh/i.test(ua) && hasTouch) ||
            (/safari/i.test(ua) &&
                hasTouch &&
                !/chrome/i.test(ua) &&
                navigator.maxTouchPoints > 1);

        // 4. Check for mobile phones (smaller screens)
        const isMobilePhone =
            /mobile|phone|iphone|ipod|android.*mobile|blackberry|opera mini|windows phone/i.test(
                ua
            ) && minDimension < 600;

        // 5. Tablet size characteristics - more lenient
        // Tablets typically 600px-1400px, aspect ratio 1.2-1.8
        const isTabletSize =
            minDimension >= 600 && maxDimension <= 1400 && aspectRatio < 2.5;

        // 6. Touch-enabled medium/large screen (likely tablet)
        const isTouchLargeScreen = hasTouch && minDimension >= 600;

        // 7. Check for common tablet keywords in user agent
        const hasTabletKeyword =
            /tablet|ipad|playbook|silk|kindle|nexus 7|nexus 9|nexus 10|xoom|sch-i800|sm-t|tab-|gt-p|gt-n|sgh-t|lenovo|mediapad|galaxy tab/i.test(
                ua
            );

        // 8. Check if it's a Windows tablet (Surface, etc.)
        const isWindowsTablet =
            /windows/i.test(ua) &&
            hasTouch &&
            (minDimension >= 600 || /tablet pc/i.test(ua) || /touch/i.test(ua));

        // More lenient: if SDK says mobile/tablet OR it matches any tablet characteristic
        const isMobileOrTablet =
            sdkMobile ||
            sdkTablet ||
            isAndroidTablet ||
            isIPad ||
            isTabletSize ||
            hasTabletKeyword ||
            isMobilePhone ||
            isTouchLargeScreen ||
            isWindowsTablet;

        // Device detection debug removed for performance

        return isMobileOrTablet;
    };

    const isMobileOrTablet = detectMobileOrTablet();

    // Development mode bypass - remove or set to false in production
    const ALLOW_DESKTOP_TESTING = false; // Set to true to test on desktop/laptop

    // URL parameter bypass: add ?bypass=true to URL to force allow any device
    const urlParams = new URLSearchParams(window.location.search);
    const bypassCheck = urlParams.get("bypass") === "true";

    const navigate = useNavigate();
    const settingsRef = useRef<HTMLDivElement>(null);
    const userDetails = localStorage.getItem("userDetails");

    useDisableZoom();

    // User verification effect
    useEffect(() => {
        const verifyUser = async (userData) => {
            try {
                const response = await axios.get(Constants.API.VERIFY_USER, {
                    params: { email: userData.email },
                    headers: { Accept: "application/json" },
                });

                const responseData = response.data;
                if (
                    responseData.data?.status &&
                    responseData.data.client?.status &&
                    responseData.data.client_id === userData.client_id
                ) {
                    setIsValidUser(true);
                }
            } catch (error) {
                logger.warn("user_verification_failed", { error: describeError(error) });
            } finally {
                setIsLoading(false);
            }
        };

        if (userDetails) {
            verifyUser(JSON.parse(userDetails));
        } else {
            setIsLoading(false);
        }
    }, []);

    // Camera selection effect
    useEffect(() => {
        if (cameras?.length) {
            const firstCamera = cameras[0];
            // Set cameraId - if empty, set to special value that SDK can handle
            if (firstCamera.deviceId && firstCamera.deviceId.trim() !== '') {
                setCameraId(firstCamera.deviceId);
            } else {
                // If deviceId is empty (no permission yet), SDK will auto-select
                setCameraId('');
            }
        }
    }, [cameras]);

    const onSettingsClickedHandler = useCallback((event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const isSettingsButtonClicked = target?.id === "settingsButton";
        const isInsideSettingsClicked =
            settingsRef.current?.contains(target) || isSettingsButtonClicked;

        if (!isInsideSettingsClicked) {
            setIsSettingsOpen(false);
        }
    }, []);

    useEffect(() => {
        document.addEventListener("click", onSettingsClickedHandler);
        return () =>
            document.removeEventListener("click", onSettingsClickedHandler);
    }, [onSettingsClickedHandler]);

    const updateLicenseStatus = useCallback((valid) => {
        setIsLicenseValid(valid);
    }, []);

    const toggleSettingsClick = useCallback(() => {
        setIsSettingsOpen((prev) => !prev);
    }, []);

    const handleCloseSettings = useCallback(({ cameraId }) => {
        setCameraId(cameraId);
        setIsSettingsOpen(false);
    }, []);

    // Show warning toast if bypassed - must be before early returns
    useEffect(() => {
        if (bypassCheck && !isMobileOrTablet) {
            toast("⚠️ Device check bypassed - testing mode active", {
                duration: 4000,
                style: {
                    background: "#f59e0b",
                    color: "#fff",
                },
            });
        }
    }, [bypassCheck, isMobileOrTablet]);

    if (isLoading) {
        return <Loader />;
    }

    // Block access for non-mobile/tablet devices (unless development mode or bypass enabled)
    if (!isMobileOrTablet && !ALLOW_DESKTOP_TESTING && !bypassCheck) {
        return (
            <UnsupportedDeviceContainer>
                <Toaster position="top-center" reverseOrder={false} />
                <UnsupportedContent>
                    <WarningIcon>⚠️</WarningIcon>
                    <Title>Device Not Supported</Title>
                    <Message>
                        This health scanning feature requires a mobile device or tablet with
                        a camera.
                    </Message>
                    <SubMessage>
                        Please access this page from your smartphone or tablet to use the
                        vital signs monitoring.
                    </SubMessage>
                    <SubMessage
                        style={{ marginTop: "16px", fontSize: "12px", color: "#999" }}
                    >
                        Debug info logged to console. Check browser developer tools for
                        details.
                    </SubMessage>
                </UnsupportedContent>
            </UnsupportedDeviceContainer>
        );
    }

    return (
        <Container isSettingsOpen={isSettingsOpen}>
            {/* Place toaster once at app/page root */}
            <Toaster position="top-center" reverseOrder={false} />

            <ZakHealthSignalMonitor
                showMonitor={!(isMobileOrTablet && isSettingsOpen)}
                cameraId={cameraId}
                onLicenseStatus={updateLicenseStatus}
                onSettingsClick={toggleSettingsClick}
                isSettingsOpen={isSettingsOpen}
            />
            {/* <SettingsBars
        open={isSettingsOpen}
        onClose={handleCloseSettings}
        cameras={cameras}
        isLicenseValid={isLicenseValid}
      /> */}
        </Container>
    );
};

export default ScanPage;
